import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import { SECTION_OPTIONS } from "./ColumnSelectionReviewStep";
import BaseDialog from "../../utils/dialog/BaseDialog";
import InsightsIcon from "@mui/icons-material/Insights";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const IGNORE_SECTION = "Ignore";
const IGNORE_LABEL = "Original Subtotals / Ignored";

const SUMMARY_PAGES = [
  { id: "overall", label: "Summary" },
  {
    id: "plCategory",
    optionKey: "plCategory",
    label: "P&L sections"
  },
  {
    id: "taxAdjustment",
    optionKey: "taxAdjustment",
    label: "Tax treatment"
  },
  {
    id: "merge",
    optionKey: "mergeAccounts",
    label: "Combined accounts"
  }
];

const displaySectionLabel = (section) =>
  section === IGNORE_SECTION ? IGNORE_LABEL : section || "Unassigned";

const formatFinancialAmount = (amount) => {
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) {
    return { text: "—", negative: false };
  }
  return {
    text: formatAmount(numericValue, false, false, false, true),
    negative: numericValue < 0
  };
};

const FinancialAmount = ({ amount, fontWeight, variant = "body1" }) => {
  const { text, negative } = formatFinancialAmount(amount);
  return (
    <Typography
      variant={variant}
      sx={{
        color: negative ? "error.main" : "text.primary",
        fontWeight: fontWeight || (negative ? 600 : 500)
      }}
    >
      {text}
    </Typography>
  );
};

const normalizeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeTaxValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text || text === "None") return "";
  return text;
};

const originalRowName = (row) => row?.accountName || row?.name || "";

const findOriginalRow = (originalRows, name, amount, usedKeys) => {
  const needle = normalizeName(name);
  const numericAmount = Number(amount);
  const exact = originalRows.find((row) => {
    const key = row.row_id || `${originalRowName(row)}-${row.amount}`;
    if (usedKeys.has(key)) return false;
    return (
      normalizeName(originalRowName(row)) === needle &&
      Number(row.amount) === numericAmount
    );
  });
  if (exact) return exact;
  return originalRows.find((row) => {
    const key = row.row_id || `${originalRowName(row)}-${row.amount}`;
    if (usedKeys.has(key)) return false;
    return normalizeName(originalRowName(row)) === needle;
  });
};

const taxDisplayLabel = (value, taxAdjustmentOptions) => {
  const normalized = normalizeTaxValue(value);
  if (!normalized) return "No tax treatment";
  return (
    taxAdjustmentOptions.find(
      (option) => option.value === normalized || option.label === normalized
    )?.label || normalized
  );
};

const flattenClassifiedSources = (rows, originalRows) => {
  const usedKeys = new Set();
  const items = [];

  (rows || []).forEach((row) => {
    const sources =
      (row.history || []).length > 0
        ? row.history
        : [
            {
              name: row.name,
              amount: row.amount,
              accountCode: row.accountCode,
              taxAdjustment: row.taxAdjustment
            }
          ];

    sources.forEach((source, sourceIndex) => {
      const reviewedSource = row.unmerged
        ? row.childRows?.[sourceIndex] || source
        : source;
      const original = findOriginalRow(
        originalRows,
        source.name,
        source.amount,
        usedKeys
      );
      if (original) {
        usedKeys.add(
          original.row_id || `${originalRowName(original)}-${original.amount}`
        );
      }
      items.push({
        id: `${row.row_id}-${sourceIndex}`,
        sourceName: source.name || originalRowName(original) || "Untitled",
        sourceAmount: source.amount ?? original?.amount ?? "",
        beforeSection: original ? original.section || "" : source.section || "",
        afterSection: row.section || "",
        beforeTax: original
          ? (original.taxAdjustment ?? "")
          : (source.taxAdjustment ?? ""),
        afterTax: row.unmerged
          ? (reviewedSource.taxAdjustment ?? row.taxAdjustment ?? "")
          : (row.taxAdjustment ?? ""),
        classifiedName: row.unmerged ? reviewedSource.name : row.name
      });
    });
  });

  return items;
};

const ChangeStatus = ({ before, after, emptyLabel = "Unassigned" }) => {
  const beforeText = before || emptyLabel;
  const afterText = after || emptyLabel;
  if (beforeText === afterText) {
    return <Chip size='small' variant='outlined' label='Unchanged' />;
  }
  if (!before) {
    return <Chip size='small' color='primary' label='Assigned' />;
  }
  return (
    <Chip size='small' color='warning' variant='outlined' label='Changed' />
  );
};

const sumAmount = (rows) =>
  (rows || []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

const countLabel = (count, label) =>
  `${count} ${label}${count === 1 ? "" : "s"}`;

const isIgnoredRow = (row) => row?.section === IGNORE_SECTION;

const availableRows = (rows) =>
  (rows || []).filter((row) => !isIgnoredRow(row));

const ignoredRows = (rows) => (rows || []).filter(isIgnoredRow);

const sectionAmount = (rows, section) =>
  sumAmount((rows || []).filter((row) => row.section === section));

const buildProfitStatement = (rows) => {
  const revenue = sectionAmount(rows, "Revenue");
  const otherIncome = sectionAmount(rows, "Other Income");
  const costOfSales = sectionAmount(rows, "Cost of Sales");
  const expenses = sectionAmount(rows, "Expenses");
  const grossProfit = revenue - costOfSales;
  const netProfit = revenue + otherIncome - costOfSales - expenses;
  return {
    revenue,
    otherIncome,
    costOfSales,
    expenses,
    grossProfit,
    netProfit
  };
};

const changedRowSx = (changed) =>
  changed
    ? { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08) }
    : undefined;

const SummaryTable = ({ label, children, minWidth = 520 }) => (
  <TableContainer role='region' aria-label={label} tabIndex={0}>
    <Table aria-label={label} sx={{ minWidth }}>
      {children}
    </Table>
  </TableContainer>
);

const SectionTitle = ({ children }) => (
  <Typography
    component='h3'
    variant='h6'
    sx={{ fontSize: 18, fontWeight: 700, mb: 1.5 }}
  >
    {children}
  </Typography>
);

const SummaryMetric = ({ label, children, note, highlight = false }) => (
  <Box
    sx={{
      p: 2,
      minWidth: 0,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      bgcolor: highlight
        ? (theme) => alpha(theme.palette.primary.main, 0.05)
        : "background.paper"
    }}
  >
    <Typography sx={{ fontWeight: 600, mb: 0.75 }}>{label}</Typography>
    <Box
      sx={{
        fontSize: 28,
        fontWeight: 700,
        overflowWrap: "anywhere"
      }}
    >
      {children}
    </Box>
    <Typography color='text.secondary' sx={{ mt: 0.5 }}>
      {note}
    </Typography>
  </Box>
);

const ChangeFilter = ({ value, onChange, changed, total }) => (
  <ToggleButtonGroup
    exclusive
    value={value}
    onChange={(_event, next) => {
      if (next !== null) onChange(next);
    }}
    aria-label='Accounts to show'
    sx={{ mb: 2, flexWrap: "wrap" }}
  >
    <ToggleButton value='changes'>Changes ({changed})</ToggleButton>
    <ToggleButton value='all'>All ({total})</ToggleButton>
  </ToggleButtonGroup>
);

const ClassificationSummaryDialog = ({
  open,
  onClose,
  originalRows = [],
  classifiedRows = [],
  profit,
  currency,
  failures = [],
  classifyOptions,
  taxAdjustmentOptions = []
}) => {
  const [pageId, setPageId] = useState("overall");
  const [sectionFilter, setSectionFilter] = useState("changes");
  const [taxFilter, setTaxFilter] = useState("changes");
  const enabledTasks = useMemo(
    () => ({
      plCategory: Boolean(classifyOptions?.plCategory),
      taxAdjustment: Boolean(classifyOptions?.taxAdjustment),
      mergeAccounts: Boolean(classifyOptions?.mergeAccounts)
    }),
    [
      classifyOptions?.mergeAccounts,
      classifyOptions?.plCategory,
      classifyOptions?.taxAdjustment
    ]
  );
  const availablePages = useMemo(
    () =>
      SUMMARY_PAGES.filter(
        (item) => !item.optionKey || enabledTasks[item.optionKey]
      ),
    [enabledTasks]
  );

  useEffect(() => {
    if (open) {
      setPageId("overall");
      setSectionFilter("changes");
      setTaxFilter("changes");
    }
  }, [open]);

  useEffect(() => {
    if (!availablePages.some((item) => item.id === pageId)) {
      setPageId("overall");
    }
  }, [availablePages, pageId]);

  const sourceChanges = useMemo(
    () => flattenClassifiedSources(classifiedRows, originalRows),
    [classifiedRows, originalRows]
  );
  const sectionMoves = useMemo(
    () =>
      sourceChanges.filter(
        (item) => (item.beforeSection || "") !== (item.afterSection || "")
      ),
    [sourceChanges]
  );
  const taxMoves = useMemo(
    () =>
      sourceChanges.filter(
        (item) =>
          normalizeTaxValue(item.beforeTax) !== normalizeTaxValue(item.afterTax)
      ),
    [sourceChanges]
  );
  const mergedRows = useMemo(
    () =>
      classifiedRows.filter(
        (row) => !row.unmerged && (row.history || []).length > 1
      ),
    [classifiedRows]
  );
  const beforeProfit = useMemo(
    () => buildProfitStatement(originalRows),
    [originalRows]
  );
  const hasBeforeSections = useMemo(
    () => originalRows.some((row) => Boolean(row.section)),
    [originalRows]
  );
  const beforeAvailable = useMemo(
    () => availableRows(originalRows),
    [originalRows]
  );
  const afterAvailable = useMemo(
    () => availableRows(classifiedRows),
    [classifiedRows]
  );
  const beforeIgnored = useMemo(
    () => ignoredRows(originalRows),
    [originalRows]
  );
  const afterIgnored = useMemo(
    () => ignoredRows(classifiedRows),
    [classifiedRows]
  );
  const beforeTotal = useMemo(
    () => sumAmount(beforeAvailable),
    [beforeAvailable]
  );
  const afterTotal = useMemo(() => sumAmount(afterAvailable), [afterAvailable]);
  const comparisonLines = useMemo(() => {
    const pairs = [
      { label: "Revenue", before: beforeProfit.revenue, after: profit.revenue },
      {
        label: "Cost of Sales",
        before: -Math.abs(beforeProfit.costOfSales),
        after: -Math.abs(profit.costOfSales)
      },
      {
        label: "Gross Profit",
        before: beforeProfit.grossProfit,
        after: profit.grossProfit,
        emphasize: true
      },
      {
        label: "Other Income",
        before: beforeProfit.otherIncome,
        after: profit.otherIncome
      },
      {
        label: "Expenses",
        before: -Math.abs(beforeProfit.expenses),
        after: -Math.abs(profit.expenses)
      },
      {
        label: "Net profit before tax",
        before: beforeProfit.netProfit,
        after: profit.netProfit,
        total: true
      }
    ];
    return pairs.map((line) => ({
      ...line,
      change: line.after - line.before
    }));
  }, [beforeProfit, profit]);
  const sectionSummary = useMemo(() => {
    const keys = [...SECTION_OPTIONS, "Unassigned"];
    return keys
      .map((section) => {
        const beforeRows = originalRows.filter((row) =>
          section === "Unassigned" ? !row.section : row.section === section
        );
        const afterRows = classifiedRows.filter((row) =>
          section === "Unassigned" ? !row.section : row.section === section
        );
        return {
          section,
          beforeCount: beforeRows.length,
          afterCount: afterRows.length,
          beforeAmount: sumAmount(beforeRows),
          afterAmount: sumAmount(afterRows)
        };
      })
      .filter(
        (item) =>
          item.beforeCount > 0 ||
          item.afterCount > 0 ||
          SECTION_OPTIONS.includes(item.section)
      );
  }, [originalRows, classifiedRows]);
  const sortedSectionRows = useMemo(
    () =>
      [...sourceChanges].sort((left, right) => {
        const leftChanged =
          (left.beforeSection || "") !== (left.afterSection || "");
        const rightChanged =
          (right.beforeSection || "") !== (right.afterSection || "");
        return Number(rightChanged) - Number(leftChanged);
      }),
    [sourceChanges]
  );
  const sortedTaxRows = useMemo(
    () =>
      [...sourceChanges].sort((left, right) => {
        const leftChanged =
          normalizeTaxValue(left.beforeTax) !==
          normalizeTaxValue(left.afterTax);
        const rightChanged =
          normalizeTaxValue(right.beforeTax) !==
          normalizeTaxValue(right.afterTax);
        return Number(rightChanged) - Number(leftChanged);
      }),
    [sourceChanges]
  );

  const currencyLabel =
    currency === "MYR" ? "RM" : currency === "SGD" ? "S$" : currency;
  const amountHeading = currencyLabel ? `Amount (${currencyLabel})` : "Amount";
  const mergedSourceCount = mergedRows.reduce(
    (sum, row) => sum + row.history.length,
    0
  );
  const splitRows = classifiedRows.filter(
    (row) => row.unmerged && (row.history || []).length > 1
  );
  const visibleSectionRows =
    sectionFilter === "changes" ? sectionMoves : sortedSectionRows;
  const visibleTaxRows = taxFilter === "changes" ? taxMoves : sortedTaxRows;
  const taskResults = {
    plCategory: `${countLabel(sectionMoves.length, "source row")} updated`,
    taxAdjustment: `${countLabel(taxMoves.length, "source row")} updated`,
    merge: `${countLabel(mergedSourceCount, "source row")} → ${countLabel(mergedRows.length, "combined account")}`
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      icon={<InsightsIcon />}
      title='Processing summary'
      maxWidth='lg'
      paperSx={{
        m: { xs: 1, sm: 2 },
        width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
        height: "calc(100dvh - 32px)",
        maxHeight: "calc(100dvh - 32px)",
        minHeight: 0,
        overflow: "hidden"
      }}
      contentSx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        p: 0
      }}
      actionsSx={{ py: 1.5, flexShrink: 0 }}
      actions={
        <Button
          variant='contained'
          onClick={() => onClose()}
          sx={{ minHeight: 44, fontSize: 16, textTransform: "none", px: 3 }}
        >
          Back to review
        </Button>
      }
    >
      <Tabs
        value={pageId}
        onChange={(_event, nextPageId) => setPageId(nextPageId)}
        variant='scrollable'
        allowScrollButtonsMobile
        aria-label='Processing summary views'
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          px: { xs: 1, sm: 2 },
          "& .MuiTab-root": {
            minHeight: 52,
            px: 2,
            fontSize: 16,
            fontWeight: 600,
            textTransform: "none"
          }
        }}
      >
        {availablePages.map((item) => (
          <Tab
            key={item.id}
            value={item.id}
            label={item.label}
            id={`summary-tab-${item.id}`}
            aria-controls={`summary-panel-${item.id}`}
          />
        ))}
      </Tabs>
      <Box
        role='tabpanel'
        id={`summary-panel-${pageId}`}
        aria-labelledby={`summary-tab-${pageId}`}
        key={pageId}
        tabIndex={0}
        sx={{
          minHeight: 0,
          minWidth: 0,
          flex: 1,
          p: { xs: 2, sm: 3 },
          overflowY: "auto",
          "& .MuiTypography-body1, & .MuiTypography-body2": {
            fontSize: 16,
            lineHeight: 1.5
          },
          "& .MuiButton-root, & .MuiToggleButton-root": {
            minHeight: 44,
            fontSize: 16,
            textTransform: "none"
          },
          "& .MuiChip-root": { fontSize: 14, height: "auto", minHeight: 28 },
          "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 },
          "& .MuiTableContainer-root": {
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2
          },
          "& .MuiTableCell-root": {
            fontSize: 16,
            px: 2,
            py: 1.5,
            verticalAlign: "top",
            overflowWrap: "anywhere"
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: "action.hover",
            color: "text.primary",
            fontWeight: 700
          },
          "& .MuiTableCell-alignRight": {
            whiteSpace: "nowrap"
          },
          "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
            { borderBottom: 0 },
          "& .MuiAccordion-root": {
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            boxShadow: "none",
            "&:before": { display: "none" }
          },
          "& .MuiAccordionSummary-root": { minHeight: 52 },
          "& :focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: -2
          }
        }}
      >
        <Typography color='text.secondary' sx={{ mb: 2 }}>
          Original = imported data. Current = results with your review edits.
        </Typography>
        {failures.length > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <Typography fontWeight={700}>
              {countLabel(failures.length, "account row")}{" "}
              {failures.length === 1 ? "needs" : "need"} attention
            </Typography>
            Add or correct the name, amount or section in Review.
          </Alert>
        )}

        {pageId === "overall" && (
          <Stack spacing={3}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr 1fr" },
                gap: 2
              }}
            >
              <SummaryMetric
                label='Account rows'
                note={`${countLabel(afterIgnored.length, "excluded row")} not counted`}
              >
                {afterAvailable.length}
              </SummaryMetric>
              <SummaryMetric
                label='Net profit before tax'
                note={
                  currencyLabel
                    ? `Current result · ${currencyLabel}`
                    : "Current result"
                }
                highlight
              >
                <FinancialAmount
                  amount={profit.netProfit}
                  variant='h5'
                  fontWeight={700}
                />
              </SummaryMetric>
              <SummaryMetric
                label='Missing details'
                note={
                  failures.length
                    ? "Return to review to complete"
                    : "No missing name, amount or section"
                }
              >
                {failures.length}
              </SummaryMetric>
            </Box>

            <Box>
              <SectionTitle>What changed?</SectionTitle>
              <SummaryTable label='Processing results'>
                <TableHead>
                  <TableRow>
                    <TableCell>Processing</TableCell>
                    <TableCell>Current result</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {SUMMARY_PAGES.filter((item) => item.optionKey).map(
                    (item) => (
                      <TableRow key={item.id}>
                        <TableCell
                          component='th'
                          scope='row'
                          sx={{ fontWeight: 600 }}
                        >
                          {item.label}
                        </TableCell>
                        <TableCell>
                          {enabledTasks[item.optionKey]
                            ? taskResults[item.id]
                            : "Not selected"}
                        </TableCell>
                        <TableCell>
                          {enabledTasks[item.optionKey] ? (
                            <Button
                              onClick={() => setPageId(item.id)}
                              endIcon={<ArrowForwardIcon />}
                              aria-label={`View ${item.label.toLowerCase()}`}
                            >
                              View
                            </Button>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </SummaryTable>
            </Box>

            <Box>
              <SectionTitle>
                Profit &amp; loss{currencyLabel ? ` (${currencyLabel})` : ""}
              </SectionTitle>
              {!hasBeforeSections && (
                <Typography color='text.secondary' sx={{ mb: 1.5 }}>
                  Original totals are unavailable: imported rows had no P&amp;L
                  sections.
                </Typography>
              )}
              <SummaryTable label='Profit and loss comparison'>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align='right'>Original</TableCell>
                    <TableCell align='right'>Current</TableCell>
                    {hasBeforeSections && (
                      <TableCell align='right'>Difference</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonLines.map((line) => (
                    <TableRow
                      key={line.label}
                      sx={
                        line.total
                          ? {
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.08)
                            }
                          : undefined
                      }
                    >
                      <TableCell
                        component='th'
                        scope='row'
                        sx={{
                          fontWeight: line.total || line.emphasize ? 700 : 400
                        }}
                      >
                        {line.label}
                      </TableCell>
                      <TableCell align='right'>
                        {hasBeforeSections ? (
                          <FinancialAmount amount={line.before} />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        <FinancialAmount
                          amount={line.after}
                          fontWeight={line.total || line.emphasize ? 700 : 500}
                        />
                      </TableCell>
                      {hasBeforeSections && (
                        <TableCell align='right'>
                          <FinancialAmount amount={line.change} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </SummaryTable>
            </Box>

            <Accordion disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id='account-totals-heading'
                aria-controls='account-totals-content'
              >
                <Typography fontWeight={600}>
                  Account totals &amp; excluded rows
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color='text.secondary' sx={{ mb: 1.5 }}>
                  Excluded rows are original subtotals or rows marked Ignore.
                </Typography>
                <SummaryTable label='Account totals and excluded rows'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align='right'>Original</TableCell>
                      <TableCell align='right'>Current</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component='th' scope='row'>
                        Account rows
                      </TableCell>
                      <TableCell align='right'>
                        {beforeAvailable.length}
                      </TableCell>
                      <TableCell align='right'>
                        {afterAvailable.length}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component='th' scope='row'>
                        Account total
                        {currencyLabel ? ` (${currencyLabel})` : ""}
                      </TableCell>
                      <TableCell align='right'>
                        <FinancialAmount amount={beforeTotal} />
                      </TableCell>
                      <TableCell align='right'>
                        <FinancialAmount amount={afterTotal} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component='th' scope='row'>
                        Excluded rows
                      </TableCell>
                      <TableCell align='right'>
                        {beforeIgnored.length}
                      </TableCell>
                      <TableCell align='right'>{afterIgnored.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component='th' scope='row'>
                        Excluded total
                        {currencyLabel ? ` (${currencyLabel})` : ""}
                      </TableCell>
                      <TableCell align='right'>
                        <FinancialAmount amount={sumAmount(beforeIgnored)} />
                      </TableCell>
                      <TableCell align='right'>
                        <FinancialAmount amount={sumAmount(afterIgnored)} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </SummaryTable>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}

        {pageId === "plCategory" && (
          <Stack spacing={3}>
            <Box>
              <SectionTitle>Current P&amp;L sections</SectionTitle>
              <SummaryTable label='Current P&L sections'>
                <TableHead>
                  <TableRow>
                    <TableCell>Section</TableCell>
                    <TableCell align='right'>Account rows</TableCell>
                    <TableCell align='right'>{amountHeading}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectionSummary.map((item) => (
                    <TableRow key={item.section}>
                      <TableCell component='th' scope='row'>
                        {displaySectionLabel(item.section)}
                        {item.section === IGNORE_SECTION && (
                          <Typography color='text.secondary'>
                            Not counted in totals
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align='right'>{item.afterCount}</TableCell>
                      <TableCell align='right'>
                        <FinancialAmount amount={item.afterAmount} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.06)
                    }}
                  >
                    <TableCell
                      component='th'
                      scope='row'
                      sx={{ fontWeight: 700 }}
                    >
                      Total excluding ignored rows
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700 }}>
                      {afterAvailable.length}
                    </TableCell>
                    <TableCell align='right'>
                      <FinancialAmount amount={afterTotal} fontWeight={700} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </SummaryTable>
            </Box>
            <Box>
              <SectionTitle>Section changes</SectionTitle>
              <ChangeFilter
                value={sectionFilter}
                onChange={setSectionFilter}
                changed={sectionMoves.length}
                total={sourceChanges.length}
              />
              {visibleSectionRows.length === 0 ? (
                <Alert severity='info'>
                  {sourceChanges.length
                    ? "No section changes. Select All to see current assignments."
                    : "No accounts to show."}
                </Alert>
              ) : (
                <SummaryTable label='Section changes' minWidth={720}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Original account</TableCell>
                      <TableCell align='right'>{amountHeading}</TableCell>
                      <TableCell>Original section</TableCell>
                      <TableCell>Current section</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleSectionRows.map((item) => (
                      <TableRow
                        key={item.id}
                        sx={changedRowSx(
                          (item.beforeSection || "") !==
                            (item.afterSection || "")
                        )}
                      >
                        <TableCell component='th' scope='row'>
                          {item.sourceName}
                        </TableCell>
                        <TableCell align='right'>
                          <FinancialAmount amount={item.sourceAmount} />
                        </TableCell>
                        <TableCell>
                          {displaySectionLabel(item.beforeSection)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {displaySectionLabel(item.afterSection)}
                        </TableCell>
                        <TableCell>
                          <ChangeStatus
                            before={item.beforeSection}
                            after={item.afterSection}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SummaryTable>
              )}
            </Box>
          </Stack>
        )}

        {pageId === "taxAdjustment" && (
          <Box>
            <SectionTitle>Tax treatment changes</SectionTitle>
            <ChangeFilter
              value={taxFilter}
              onChange={setTaxFilter}
              changed={taxMoves.length}
              total={sourceChanges.length}
            />
            {visibleTaxRows.length === 0 ? (
              <Alert severity='info'>
                {sourceChanges.length
                  ? "No tax treatment changes. Select All to see current assignments."
                  : "No accounts to show."}
              </Alert>
            ) : (
              <SummaryTable label='Tax treatment changes' minWidth={680}>
                <TableHead>
                  <TableRow>
                    <TableCell>Original account</TableCell>
                    <TableCell>Original treatment</TableCell>
                    <TableCell>Current treatment</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleTaxRows.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={changedRowSx(
                        normalizeTaxValue(item.beforeTax) !==
                          normalizeTaxValue(item.afterTax)
                      )}
                    >
                      <TableCell component='th' scope='row'>
                        {item.sourceName}
                        {item.classifiedName &&
                          item.classifiedName !== item.sourceName && (
                            <Typography color='text.secondary'>
                              Now: {item.classifiedName}
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell>
                        {taxDisplayLabel(item.beforeTax, taxAdjustmentOptions)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {taxDisplayLabel(item.afterTax, taxAdjustmentOptions)}
                      </TableCell>
                      <TableCell>
                        <ChangeStatus
                          before={normalizeTaxValue(item.beforeTax)}
                          after={normalizeTaxValue(item.afterTax)}
                          emptyLabel='No tax treatment'
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SummaryTable>
            )}
          </Box>
        )}

        {pageId === "merge" && (
          <Stack spacing={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 2
              }}
            >
              <Box>
                <Typography variant='h5' fontWeight={700}>
                  {mergedSourceCount}
                </Typography>
                <Typography>Original rows</Typography>
              </Box>
              <ArrowForwardIcon
                aria-hidden='true'
                sx={{ color: "text.secondary" }}
              />
              <Box>
                <Typography variant='h5' fontWeight={700}>
                  {mergedRows.length}
                </Typography>
                <Typography>Combined accounts</Typography>
              </Box>
            </Box>
            {mergedRows.length === 0 ? (
              <Alert severity='info'>No accounts are currently combined.</Alert>
            ) : (
              <Box>
                <SectionTitle>Which accounts were combined?</SectionTitle>
                <SummaryTable label='Combined accounts' minWidth={760}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Original accounts</TableCell>
                      <TableCell>Current account</TableCell>
                      <TableCell align='right'>
                        Original total
                        {currencyLabel ? ` (${currencyLabel})` : ""}
                      </TableCell>
                      <TableCell align='right'>
                        Current total
                        {currencyLabel ? ` (${currencyLabel})` : ""}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mergedRows.map((row) => (
                      <TableRow key={row.row_id}>
                        <TableCell>
                          <Stack
                            component='ul'
                            spacing={1}
                            sx={{ m: 0, p: 0, listStyle: "none" }}
                          >
                            {row.history.map((item, index) => (
                              <Box
                                component='li'
                                key={`${row.row_id}-${index}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 2
                                }}
                              >
                                <Typography>
                                  {item.name || "Untitled"}
                                </Typography>
                                <FinancialAmount amount={item.amount} />
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell
                          component='th'
                          scope='row'
                          sx={{ fontWeight: 600 }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell align='right'>
                          <FinancialAmount amount={sumAmount(row.history)} />
                        </TableCell>
                        <TableCell align='right'>
                          <FinancialAmount
                            amount={row.amount}
                            fontWeight={700}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SummaryTable>
              </Box>
            )}
            {splitRows.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  id='split-accounts-heading'
                  aria-controls='split-accounts-content'
                >
                  <Typography fontWeight={600}>
                    Split back into separate accounts (
                    {countLabel(splitRows.length, "group")})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SummaryTable label='Accounts split during review'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Previously combined</TableCell>
                        <TableCell>Current separate accounts</TableCell>
                        <TableCell align='right'>{amountHeading}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {splitRows.map((row) => (
                        <TableRow key={row.row_id}>
                          <TableCell component='th' scope='row'>
                            {row.name}
                          </TableCell>
                          <TableCell>
                            {(row.childRows?.length
                              ? row.childRows
                              : row.history
                            ).map((child, index) => (
                              <Typography key={index}>
                                {child.name || "Untitled"}
                              </Typography>
                            ))}
                          </TableCell>
                          <TableCell align='right'>
                            <FinancialAmount amount={row.amount} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </SummaryTable>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        )}
      </Box>
    </BaseDialog>
  );
};

export default ClassificationSummaryDialog;
